import { useState, useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import logoCompass from '@/assets/logo-compass.png';
import logoWordmark from '@/assets/logo-wordmark.png';
import { LibraryTab } from '@/components/tabs/LibraryTab';
import { CardsTab } from '@/components/tabs/CardsTab';
import { CommunityTab } from '@/components/tabs/CommunityTab';
import { LearnTab } from '@/components/tabs/LearnTab';
import { ProfileTab } from '@/components/tabs/ProfileTab';
import { NewsTab } from '@/components/tabs/NewsTab';
import { AuthScreen } from '@/components/AuthScreen';
import { AuthPrompt } from '@/components/AuthPrompt';
import { useAuth } from '@/hooks/useAuth';
import { useRealtimeSubscriptions } from '@/hooks/useRealtime';
import { BookOpen, Layers, MessageCircle, Library, User, LogIn, Newspaper, ChevronLeft, ChevronRight, Store } from 'lucide-react';

// Lazy: Stores pulls in Google Maps JS (heavy) — only when user opens the tab.
const StoresTab = lazy(() =>
  import('@/components/tabs/StoresTab').then(m => ({ default: m.StoresTab })),
);
// Lazy: Scout chat — only loaded after first idle so it doesn't block paint.
const HelpChat = lazy(() =>
  import('@/components/HelpChat').then(m => ({ default: m.HelpChat })),
);
// Lazy: Onboarding — only loaded for new users.
const OnboardingFlow = lazy(() =>
  import('@/components/OnboardingFlow').then(m => ({ default: m.OnboardingFlow })),
);

const TABS = [
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'library', label: 'Library', icon: Library },
  { id: 'collection', label: 'Collection', icon: Layers, requiresAuth: true },
  { id: 'stores', label: 'Stores', icon: Store },
  { id: 'community', label: 'Community', icon: MessageCircle },
  { id: 'learn', label: 'Learn', icon: BookOpen },
  { id: 'profile', label: 'Profile', icon: User, requiresAuth: true },
] as const;

const VALID_TAB_IDS = new Set(['news', 'library', 'collection', 'stores', 'community', 'learn', 'profile']);

function parseHash(): { tab?: string; subTab?: string; anchor?: string } {
  const raw = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#\/?/, '');
  if (!raw) return {};
  const [tab, subTab, anchor] = raw.split('/');
  return { tab, subTab, anchor };
}

const Index = () => {
  const { user, profile, loading } = useAuth();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const showOnboarding =
    !loading && !!user && !!profile && !profile.onboarding_completed_at && !onboardingDismissed;
  const initial = parseHash();
  const [activeTab, setActiveTab] = useState(
    initial.tab && VALID_TAB_IDS.has(initial.tab) ? initial.tab : 'news',
  );
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [recentTabs, setRecentTabs] = useState<string[]>(
    initial.tab && VALID_TAB_IDS.has(initial.tab) ? [initial.tab] : [],
  );
  const [helpChatMounted, setHelpChatMounted] = useState(false);

  const queryClient = useQueryClient();
  useRealtimeSubscriptions();

  // Defer mounting Scout until the browser is idle — keeps it out of the
  // initial JS payload and off the critical path for LCP.
  useEffect(() => {
    const w: any = window;
    const idle = w.requestIdleCallback || ((cb: any) => setTimeout(cb, 1500));
    const handle = idle(() => setHelpChatMounted(true), { timeout: 3500 });
    return () => {
      const cancel = w.cancelIdleCallback;
      if (cancel) cancel(handle);
    };
  }, []);

  // Background-prefetch data for the heavy tabs (News + Library) so switching
  // tabs feels instant. Runs once during idle time after the initial render.
  useEffect(() => {
    const prefetch = () => {
      // News
      queryClient.prefetchQuery({
        queryKey: ['lorcana-news'],
        queryFn: async () => {
          const { data, error } = await supabase.functions.invoke('lorcana-news');
          if (error) throw error;
          return data?.items || [];
        },
        staleTime: 60 * 60 * 1000,
      });
      // Library card catalog
      queryClient.prefetchQuery({
        queryKey: ['cards', 'all'],
        queryFn: async () => {
          const PAGE_SIZE = 1000;
          const fetchPage = async (page: number) => {
            const { data, error } = await supabase
              .from('cards').select('*').order('name')
              .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
            if (error) throw error;
            return data || [];
          };
          const first = await fetchPage(0);
          if (first.length < PAGE_SIZE) return first;
          const rest = await Promise.all([fetchPage(1), fetchPage(2), fetchPage(3), fetchPage(4)]);
          return first.concat(...rest);
        },
        staleTime: 30 * 60 * 1000,
      });
      queryClient.prefetchQuery({
        queryKey: ['card-sets'],
        queryFn: async () => {
          const { data } = await supabase.from('sets').select('name').order('released_at', { ascending: false, nullsFirst: false });
          return [...new Set((data || []).map((r: any) => r.name))];
        },
        staleTime: 10 * 60 * 1000,
      });
    };
    const w: any = window;
    const idle = w.requestIdleCallback || ((cb: any) => setTimeout(cb, 400));
    const handle = idle(prefetch, { timeout: 2000 });
    return () => {
      const cancel = w.cancelIdleCallback;
      if (cancel) cancel(handle);
    };
  }, [queryClient]);


  useEffect(() => {
    const dispatchNav = (tab: string, subTab?: string, anchor?: string) => {
      window.dispatchEvent(
        new CustomEvent('lorescout:navigate-subtab', { detail: { tab, subTab, anchor } }),
      );
      if (anchor) {
        // Wait for the section to render, then scroll the anchor into view
        setTimeout(() => {
          const el = document.getElementById(`learn-${anchor}`) || document.getElementById(anchor);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 220);
      }
    };

    const onHashChange = () => {
      const { tab, subTab, anchor } = parseHash();
      if (tab && VALID_TAB_IDS.has(tab)) {
        setActiveTab(tab);
        if (subTab || anchor) {
          setTimeout(() => dispatchNav(tab, subTab, anchor), 80);
        }
      }
    };
    window.addEventListener('hashchange', onHashChange);
    if (initial.subTab || initial.anchor) {
      setTimeout(() => dispatchNav(initial.tab!, initial.subTab, initial.anchor), 120);
    }
    return () => window.removeEventListener('hashchange', onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center flex flex-col items-center">
          <img src={logoCompass} alt="LoreScout app loading" width={56} height={56} fetchPriority="high" className="h-14 w-14 animate-spin" style={{ animationDuration: '3s' }} />
        </div>
      </div>
    );
  }

  const handleTabClick = (tabId: string, subTab?: string, anchor?: string) => {
    const tab = TABS.find(t => t.id === tabId);
    if (tab && 'requiresAuth' in tab && tab.requiresAuth && !user) {
      setShowAuthModal(true);
      return;
    }
    setActiveTab(tabId);
    setRecentTabs((prev) => [tabId, ...prev.filter((t) => t !== tabId)].slice(0, 5));
    const parts = [tabId];
    if (subTab) parts.push(subTab);
    if (anchor) parts.push(anchor);
    const newHash = `#${parts.join('/')}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
    if (subTab || anchor) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('lorescout:navigate-subtab', { detail: { tab: tabId, subTab, anchor } }));
        if (anchor) {
          setTimeout(() => {
            const el = document.getElementById(`learn-${anchor}`) || document.getElementById(anchor);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 160);
        }
      }, 80);
    }
  };

  const renderTabContent = () => {
    if (activeTab === 'collection' && !user) {
      return <AuthPrompt message="Sign in to manage your collection" />;
    }
    if (activeTab === 'profile' && !user) {
      return <AuthPrompt message="Sign in to view your profile" />;
    }

    switch (activeTab) {
      case 'news': return <NewsTab />;
      case 'library': return <LibraryTab />;
      case 'collection': return <CardsTab />;
      case 'stores': return (
        <Suspense fallback={<div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading store finder…</div>}>
          <StoresTab />
        </Suspense>
      );
      case 'community': return <CommunityTab onRequestAuth={() => setShowAuthModal(true)} />;
      case 'learn': return <LearnTab />;
      case 'profile': return <ProfileTab />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <Helmet>
        <title>LoreScout — Lorcana Deck Builder, Collection Tracker & Prices</title>
        <meta name="description" content="Build Disney Lorcana decks with LoreScout's free interactive deck builder, track your collection, check live card prices, and share decklists with the community." />
        <link rel="canonical" href="https://lorescout.com/" />
        <meta property="og:title" content="LoreScout — Lorcana Deck Builder, Collection Tracker & Prices" />
        <meta property="og:description" content="Build Disney Lorcana decks with LoreScout's free interactive deck builder, track your collection, check live card prices, and share decklists with the community." />
        <meta property="og:url" content="https://lorescout.com/" />
        <meta name="twitter:title" content="LoreScout — Lorcana Deck Builder, Collection Tracker & Prices" />
        <meta name="twitter:description" content="Build Disney Lorcana decks with LoreScout's free interactive deck builder, track your collection, check live card prices, and share decklists with the community." />

      </Helmet>

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 z-40 border-r border-border bg-card/95 backdrop-blur-lg transition-[width] duration-200 ${
          sidebarCollapsed ? 'md:w-16' : 'md:w-60'
        }`}
      >
        <div className={`flex items-center gap-2 px-4 pt-5 pb-4 ${sidebarCollapsed ? 'justify-center px-2' : ''}`}>
          <img src={logoCompass} alt="" aria-hidden="true" width={32} height={32} className="h-8 w-8 flex-shrink-0" />
          {!sidebarCollapsed && (
            <img src={logoWordmark} alt="LoreScout logo" width={160} height={40} fetchPriority="high" className="w-36 object-contain" />
          )}
        </div>
        <nav className="flex-1 px-2 space-y-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                title={tab.label}
                aria-label={tab.label}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium font-body transition-colors ${
                  isActive
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{tab.label}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-2 border-t border-border">
          {!user && (
            <button
              onClick={() => setShowAuthModal(true)}
              className={`w-full flex items-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              <LogIn className="h-4 w-4" />
              {!sidebarCollapsed && <span>Sign In</span>}
            </button>
          )}
          <button
            onClick={() => setSidebarCollapsed(v => !v)}
            className="mt-2 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground font-body transition-colors"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Mobile header with tabs */}
      <header className="md:hidden sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-lg app-container w-full">
        <div className="px-4 pt-3 pb-0">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={logoCompass} alt="" aria-hidden="true" width={32} height={32} className="h-8 w-8" />
            <img src={logoWordmark} alt="LoreScout logo" width={192} height={48} fetchPriority="high" className="w-48 object-contain" />
            <img src={logoCompass} alt="" aria-hidden="true" width={32} height={32} className="h-8 w-8" />
          </div>
          <nav aria-label="Primary" className="flex">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  aria-label={tab.label}
                  className={`flex flex-1 flex-col items-center gap-0.5 pb-2.5 pt-1 transition-colors relative ${
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-[10px] font-medium font-body">{tab.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main
        className={`flex-1 overflow-y-auto transition-[margin] duration-200 ${
          sidebarCollapsed ? 'md:ml-16' : 'md:ml-60'
        }`}
      >
        <div className="app-container md:max-w-5xl md:mx-auto md:px-6 md:py-6">
          <h1 className="font-serif text-2xl md:text-3xl mb-4 px-4 md:px-0 pt-3 md:pt-0">
            Disney Lorcana Deck Builder, Collection Tracker &amp; Card Prices
          </h1>

          {renderTabContent()}
        </div>
      </main>

      {/* Floating Sign In button (mobile only) — bottom-left to avoid Scout chat */}
      {!user && !showAuthModal && (
        <button
          onClick={() => setShowAuthModal(true)}
          className="md:hidden fixed bottom-6 left-4 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground font-body shadow-lg shadow-primary/30 hover:bg-primary/90 transition-colors"
        >
          <LogIn className="h-4 w-4" /> Sign In
        </button>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm" onClick={() => setShowAuthModal(false)}>
          <div className="w-full max-w-sm mx-4 rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <AuthScreen
              onSuccess={() => setShowAuthModal(false)}
              compact
            />
          </div>
        </div>
      )}

      {/* AI help chat — deferred until idle */}
      {helpChatMounted && (
        <Suspense fallback={null}>
          <HelpChat onNavigate={(tabId, subTab, anchor) => handleTabClick(tabId, subTab, anchor)} recentTabs={recentTabs} />
        </Suspense>
      )}

      {/* First-run onboarding flow */}
      {showOnboarding && (
        <Suspense fallback={null}>
          <OnboardingFlow
            onNavigate={(tabId, subTab) => handleTabClick(tabId, subTab)}
            onComplete={() => setOnboardingDismissed(true)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
